import React, { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useVariantMappingRows } from '@/hooks/useVariantMappingRows';
import { formatFeedDate } from '@/shared/merchantFeeds';
import { SettingsRow, SettingsSection } from './SettingsGroup';

// Settings › Pricing Data In › Variant Mapping summary (FIG-321 / REQ-059).
// The mapping itself stays on Inventory › Variant Mapping. Who made the last
// change needs UC-221; the date of the latest mapping is shown.

interface VariantMappingSummaryProps {
  organisationId: string;
  /** Omit when the viewer cannot open Inventory › Variant Mapping (it is admin-only). */
  onOpenVariantMapping?: () => void;
}

export const VariantMappingSummary: React.FC<VariantMappingSummaryProps> = ({ organisationId, onOpenVariantMapping }) => {
  const { data: rows, isLoading, isError } = useVariantMappingRows(organisationId);

  const summary = useMemo(() => {
    if (!rows) return null;
    const mapped = rows.filter((r) => r.status === 'matched');
    // Rows are observed identifiers; a Variant (master product) can have several
    // (e.g. a bought-as and a sold-as SKU), so count Variants, not rows.
    const mappedVariants = new Set(mapped.map((r) => r.matchedVariant?.master_product_id).filter(Boolean)).size;
    const toMap = rows.length - mapped.length;
    const lastMapped = mapped
      .map((r) => r.matchedVariant?.matched_at)
      .filter((d): d is string => !!d)
      .sort()
      .at(-1);
    return { total: rows.length, mapped: mappedVariants, toMap, lastMapped };
  }, [rows]);

  return (
    <SettingsSection
      title="Variant Mapping"
      description="How this merchant's products are matched between how they are bought and how they are sold. Managed on the Inventory tab."
    >
      <SettingsRow
        label="Status"
        action={onOpenVariantMapping
          ? <Button type="button" variant="link" className="h-auto p-0" onClick={onOpenVariantMapping}>Open Variant Mapping</Button>
          : null}
      >
        {isLoading && (
          <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading…
          </span>
        )}
        {isError && <span role="alert" className="text-sm text-destructive">Variant Mapping could not be loaded.</span>}
        {summary && summary.total === 0 && (
          <>
            <div className="font-medium">No variants yet</div>
            <div className="mt-1 text-sm text-muted-foreground">Variants appear once inventory or sales data arrives.</div>
          </>
        )}
        {summary && summary.total > 0 && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{summary.mapped} {summary.mapped === 1 ? 'variant' : 'variants'} mapped</span>
              {summary.toMap > 0 ? (
                <span className="rounded-full border border-amber-300 bg-amber-50 px-2 text-xs font-semibold text-amber-900">
                  {summary.toMap} new {summary.toMap === 1 ? 'variant' : 'variants'} to map
                </span>
              ) : (
                <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 text-xs font-semibold text-emerald-900">All mapped</span>
              )}
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              Bought as and sold as identifiers, used by every report and calculation
              {summary.lastMapped ? ` · latest mapping ${formatFeedDate(summary.lastMapped)}` : ''}
            </div>
          </>
        )}
      </SettingsRow>
    </SettingsSection>
  );
};

export default VariantMappingSummary;
